import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { BusinessTitle, BusinessText, ErrorText, BusinessSectionTitle, BizDocBizTypeSelect, Divider, BusinessSecondTitle, DirectorDetailsContainer, InnerContainerDirectorDocs, OnboardingButtonContainer, OverallContainer, PageHeaderWrapper } from "../styles";
import Loader from "../../../components/common/Loader";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import FormSection from "../components/FormSection";
import { Controller, useForm } from "react-hook-form";
import { useBusinessTypes } from "../../../app/hooks/useBusinessTypes";
import { Types } from "../Types";
import { useFileUpload } from "../../../app/hooks/useFileUpload";
import FileUpload from "../components/FileUpload";
import CustomSelectTwo from "../../../components/common/CustomSelect/CustomSelectTwo";
import { Button } from "../../../components/common/Button";
import { addDirectorDocuments, getListOfDirectors, getParticularStep } from "../redux/actions";
import { useRequiredDocs } from "../../../app/hooks/useRequiredDocs";
import { documentSections } from "../utils/constants";
import { listOfDirectorsDocuments } from "../utils/constants";
import { updateBusinessType, updateDirectorsList, updateUploadedBusinessDocs } from "../redux/slice";
import { NetworkErrorAlertContext } from "../../../context/NetworkErrorAlert";
import { buildDirectorDocumentsUpload, extractFileNameFromUrl, getDirectorDocumentState, handleDirectorFileDelete, handleDirectorFileSelect, isDirectorDocumentSaved, isDocumentRequired } from "../utils/businessDocumentUtils";
import { useWindowSize } from "@reach/window-size";
import { getUploadedDocumentsByBusinessId } from "../../Compliance/complianceSlice";

export type DirectorDocumentState = {
  file: File | null;
  isSaved: boolean;
  uploading: boolean;
  error: string | null;
};

type Props = {
  handleNext: () => void;
  handlePrevious: () => void;
};

export type BusinessDocUploadState = {
  id: string | number;
  uploadStatus: boolean;
  name: string;
}

const BusinessDocuments = ({ handleNext, handlePrevious }: Props) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset
  } = useForm<Types>({ 
    mode: 'onChange',
    defaultValues: {
      businessType: null,
    }
   });

  const missingDocsRef = useRef({})
  const { email } = useAppSelector((state) => state.users.userObj.accountUser);
  const dispatch = useAppDispatch()
  const { width } = useWindowSize()
  const { businessTypes } = useBusinessTypes();
  const { loading } = useAppSelector((state) => state.onboarding || {});
  const { businessTypeId, directorsList, uploadedBusinessDocs } = useAppSelector((state) => state.onboarding)
  const { businessId, userObj: { accountUser : { businessCountry } } } = useAppSelector((state) => state.users);
  const [ selectedBusinessType, setSelectedBusinessType ] = useState(null)
  const { requiredDocs } = useRequiredDocs({ businessTypeId, businessCountry })
  const { onShowAlert: onShowErrorAlert } = useContext(
    NetworkErrorAlertContext,
  );

  useEffect(() => {
    const getSelectedBusinessType = () => {
      const selectedBizType = businessTypes.find((type) => type.value === businessTypeId)
      setSelectedBusinessType(selectedBizType)
    }
    getSelectedBusinessType()
  }, [businessTypeId, businessTypes])

  const setBusinessDocUploadState = (docId: string | number, uploadStatus: boolean, docFileName: string) => {
    const currentDocs = Array.isArray(uploadedBusinessDocs)
      ? [...uploadedBusinessDocs]
      : [];

    const newDoc: BusinessDocUploadState = {
      id: docId,
      uploadStatus,
      name: docFileName
    };

    const existingDocIndex = currentDocs.findIndex(doc => doc.id === docId);

    let updatedDocs;
    if (existingDocIndex !== -1) {
      updatedDocs = currentDocs.map((doc, index) =>
        index === existingDocIndex ? newDoc : doc
      );
    } else {
      updatedDocs = [...currentDocs, newDoc];
    }
    dispatch(updateUploadedBusinessDocs(updatedDocs));

  }

  const requiredDocsLookUpMap = useMemo(() => {
    return requiredDocs?.reduce((acc: Record<string, string | number>, doc) => {
      acc[doc?.document?.toLowerCase()] = doc.id
      return acc
    }, {})
  }, [requiredDocs])

  const fileStates = documentSections.map(() => useFileUpload());
  const [sectionFileUrls, setSectionFileUrls] = useState<Record<string, any>>({});

  const sectionsWithFileUpload = documentSections.map((section, index) => {
    const thisDocumentId = requiredDocsLookUpMap[section.title.toLowerCase()]

    const handleFileSelect = (selectedFile: File) => {
      const currentFileState = fileStates[index];
      currentFileState.setFile(selectedFile);
      currentFileState.uploadFile(selectedFile, businessId, thisDocumentId)
        .then(() => {
          setSectionFileUrls(prev => ({
            ...prev,
            [section.title]: { docId: thisDocumentId },
          }));
          setBusinessDocUploadState(thisDocumentId, true, selectedFile.name)
        })
        .catch((error) => {
          currentFileState.setFile(null);
          setBusinessDocUploadState(thisDocumentId, false, '')
          onShowErrorAlert("", error?.response?.data?.message || "Upload failed. Please try again.");
        });
    };

    const handleFileDelete = () => {
      const currentFileState = fileStates[index];
      currentFileState.setFile(null);
      setSectionFileUrls(prev => {
        const updated = { ...prev };
        delete updated[section.title];
        return updated;
      });

      const updatedUploadedBusDocsAfterDelete = uploadedBusinessDocs.filter(doc => doc?.businessDocument?.document?.toLowerCase() !== section.title.toLowerCase())
      
      dispatch(updateUploadedBusinessDocs(updatedUploadedBusDocsAfterDelete))
    };

    return {
      ...section,
      fileState: fileStates[index],
      handleFileSelect,
      handleFileDelete,
      shouldDisplay: isDocumentRequired(section.title, requiredDocs)
    };
  });

  const [directorDocumentStates, setDirectorDocumentStates] = useState<
    Record<number, Record<string, DirectorDocumentState>>
  >({});

  const missingBusinessDocs = sectionsWithFileUpload
    ?.filter(section => section?.shouldDisplay && section?.isRequired && !sectionFileUrls[section?.title])
    ?.map(section => section?.title) || [];

  const missingDirectorDocs = directorsList?.flatMap((dir) =>
    listOfDirectorsDocuments
      ?.filter(doc => doc?.isRequired && !getDirectorDocumentState(dir?.id, doc?.id, directorDocumentStates).file)
      ?.map(doc => `${dir?.id}-${doc?.id}`)
  ) || [];

  const allMissingDocs = [...missingBusinessDocs, ...missingDirectorDocs];


  const handleBusinessDocSubmit = async () => {

    try {   
        if (Array.isArray(allMissingDocs) && allMissingDocs?.length > 0) {
          const firstMissingDocument = allMissingDocs[0]
          const missingDocumentToHighlight = missingDocsRef.current[firstMissingDocument]

          if (missingDocumentToHighlight) {
            missingDocumentToHighlight.scrollIntoView({behavior: "smooth", block: "center"})
          }

          onShowErrorAlert("error", (`All documents must be uploaded to proceed.`));
          return
        }

        const directorDocumentPayload = buildDirectorDocumentsUpload(directorsList, directorDocumentStates)

        const directorDocsAction = await dispatch(addDirectorDocuments(directorDocumentPayload))
        
        if (addDirectorDocuments.fulfilled.match(directorDocsAction)) {
          handleNext()
        } else if (addDirectorDocuments.rejected.match(directorDocsAction)) {
          onShowErrorAlert("", String(directorDocsAction.payload));
        }

    } catch (error) {
      onShowErrorAlert('', 'There is an error. Please reload the page.');
    }
  }

  const getSavedBusinessDocs = async () => {
    const stepNumber = 5
    const action = await dispatch(getParticularStep ({businessId, stepNumber}))
    const allDirectors = await dispatch(getListOfDirectors(businessId))

    if(getParticularStep.fulfilled.match(action)) {
      dispatch(updateBusinessType(action?.payload?.businessType as any))

      const uploadedDocsAction = await dispatch(getUploadedDocumentsByBusinessId({businessId}))
      
      if (getUploadedDocumentsByBusinessId.fulfilled.match(uploadedDocsAction)) {
        const uploadedDocs = uploadedDocsAction?.payload
 
        const updatedSectionFileUrls = {}

        uploadedDocs?.forEach(doc => {
          const section = documentSections.find(s => s.title.toLowerCase() === doc?.businessDocument?.document?.toLowerCase())
          if (section) {
            updatedSectionFileUrls[section.title] = { 
              docId: doc?.businessDocument?.id 
            }
            
            const sectionIndex = documentSections.findIndex(s => s.title.toLowerCase() === doc?.businessDocument?.document?.toLowerCase())
            if (sectionIndex !== -1) {
              const currentFileState = fileStates[sectionIndex]
              
              const fileName = (doc?.url && extractFileNameFromUrl(doc.url)) || doc?.businessDocument?.document
              if (fileName) {
                const file = new File([], fileName, { type: 'image/jpeg' })
                currentFileState.setFile(file)
              }
            }
          }
        })
        
        setSectionFileUrls(updatedSectionFileUrls)
        dispatch(updateUploadedBusinessDocs(uploadedDocs))
      }
    } else if (getParticularStep.rejected.match(action)) {
      console.error('We could not retrieve previously saved data.');
    }

    if(getListOfDirectors.fulfilled.match(allDirectors)) {
      dispatch(updateDirectorsList(allDirectors.payload))

      const newDirectorDocumentStates = {}
      allDirectors?.payload?.forEach(director => {
        newDirectorDocumentStates[director.id] = {}
        
        listOfDirectorsDocuments.forEach(docType => {
          let fileUrl = null
          switch(docType.id) {
            case 'address':
              fileUrl = director.poaUrl
              break
            case 'id':
              fileUrl = director.idUrl
              break
            case 'photo':
              fileUrl = director.photographUrl
              break
          }

          if (isDirectorDocumentSaved(director, docType.id)) {
            const fileName = (fileUrl && extractFileNameFromUrl(fileUrl)) || docType.title
            
            newDirectorDocumentStates[director.id][docType.id] = {
              file: new File([], fileName, { type: 'image/jpeg' }),
              isSaved: true,
              uploading: false,
              error: null
            }
          }
        })
      })
      setDirectorDocumentStates(newDirectorDocumentStates)
    } else if (getListOfDirectors.rejected.match(allDirectors)) {
      console.error('We could not retrieve previously saved directors list.');
    }
  }
  
  useEffect(() => {
    getSavedBusinessDocs()
  }, [])

  const initialDirectorSectionDetails = {
    header: "Director's and UBO(s) details",
    headerSubline: 'We need to to identify your Directors. Please provide requested document',
    subheader: 'Ultimate Beneficial Owner',
    subheaderSubline: '(Entities with at least 20% of shares)',
  }

  const directorSectionDetails = 
    selectedBusinessType?.label === 'Incorporated Trustees/NGO'
    ? {
      ...initialDirectorSectionDetails,
      header: "Trustee member's details",
      headerSubline: "We need to to identify your Trustees. Please provide requested document",
      subheader: "Trustee",
      subheaderSubline: "",
    } : initialDirectorSectionDetails

  return (
    <OverallContainer>
      <Loader isLoading={loading} />
      <PageHeaderWrapper>
        <BusinessTitle>Business documents</BusinessTitle>
        <BusinessText>
          We need your business registration documents. This is for regulatory compliance purposes
        </BusinessText>
      </PageHeaderWrapper>
      <BizDocBizTypeSelect>
        <FormSection>
          <Controller
            name="businessType"
            control={control}
            render={({ field }) => (
              <CustomSelectTwo
                {...field}
                animatedLabel="Type of business"
                $width="100%"
                $height="48px"
                lightBorder={true}
                value={selectedBusinessType}
                disabled
              />
            )}
          />
          {errors.businessType?.type === "required" && (
            <ErrorText>
              <span>Field is required</span>
            </ErrorText>
          )}
        </FormSection>
      </BizDocBizTypeSelect>

      <div>
        {sectionsWithFileUpload?.map((section) =>
          section?.shouldDisplay && (
            <div 
              key={section?.title}
              ref={(reference) => {
                if (section?.title) {
                  missingDocsRef.current[section?.title] = reference
                }
              }}
            >
              <BusinessSectionTitle>{section?.title}</BusinessSectionTitle>
              <BusinessText alignLeft={true}>{section?.description}</BusinessText>
              <FormSection>
                <FileUpload
                  file={section?.fileState?.file ||
                    (() => {
                      if (!Array.isArray(uploadedBusinessDocs)) return false;
                      const uploadedDoc = uploadedBusinessDocs.find(doc => doc?.businessDocument?.document?.toLowerCase() === section?.title?.toLowerCase());
                      return uploadedDoc ? uploadedDoc : false;
                    })()
                  }
                  uploading={section?.fileState?.uploading ||
                    (() => {
                      if (!Array.isArray(uploadedBusinessDocs)) return undefined;
                      const uploadedDoc = uploadedBusinessDocs.find(doc => doc?.businessDocument?.document?.toLowerCase() === section?.title?.toLowerCase());
                      return uploadedDoc?.uploadStatus === true ? false : undefined;
                    })()
                  }
                  error={section?.fileState?.error}
                  onFileSelect={section?.handleFileSelect}
                  onFileDelete={section?.handleFileDelete}
                  title="Upload file"
                  subtitle="PDF, JPEG or PNG less than 5MB"
                />
                {Array.isArray(allMissingDocs) && allMissingDocs.includes(section?.title) ? (
                  <span className="error">This document is required</span>
                ) : ""}
              </FormSection>
            </div>
          )
        )}
      </div>

      <Divider></Divider>

      <DirectorDetailsContainer>
        <BusinessTitle>{directorSectionDetails?.header}</BusinessTitle>
        <BusinessText>
          {directorSectionDetails?.headerSubline}
        </BusinessText>

        {directorsList?.map((dir, directorIndex) => (
          <div key={directorIndex}>
            <BusinessSecondTitle>
              {directorSectionDetails?.subheader} ({dir.name})
            </BusinessSecondTitle>
            <BusinessText alignLeft={true}>{directorSectionDetails?.subheaderSubline}</BusinessText>

            <InnerContainerDirectorDocs>
              {listOfDirectorsDocuments?.map((section) => {
                const documentState = getDirectorDocumentState(
                  dir.id,
                  section.id,
                  directorDocumentStates
                );

                return (
                  <div 
                    key={section.title}
                    ref={(reference) => {
                      if (section?.title) {
                        missingDocsRef.current[section?.title] = reference
                      }
                    }}
                  >
                    <BusinessSectionTitle>{section.title}</BusinessSectionTitle>
                    <BusinessText alignLeft={true}>{section.description}</BusinessText>
                    <FormSection>
                      <FileUpload
                        file={documentState?.file}
                        uploading={documentState.uploading}
                        error={documentState.error}
                        onFileSelect={handleDirectorFileSelect(dir.id, section.id, setDirectorDocumentStates)}
                        onFileDelete={handleDirectorFileDelete(dir.id, section.id, setDirectorDocumentStates)}
                        title="Upload file"
                        subtitle="PDF, JPEG or PNG less than 5MB"
                      />
                      {section?.isRequired && !documentState?.file ? (
                        <span className="error">This document is required</span>
                      ) : ""}
                    </FormSection>
                  </div>
                );
              })}
            </InnerContainerDirectorDocs>
            {(directorsList?.length !== (directorIndex + 1)) && <Divider></Divider>}
          </div>
        ))}
      </DirectorDetailsContainer>

      <OnboardingButtonContainer>
        <Button
          label="Back"
          theme="alternate"
          height="48px"
          fontSize="16px"
          width="150px"
          onClick={handlePrevious}
        />
        <Button
          onClick={handleSubmit(handleBusinessDocSubmit)}
          label="Continue"
          width={width < 1150 ? '' : '494px' }
          height="48px"
          fontSize="16px"
          disabled={loading}
        />
      </OnboardingButtonContainer>
    </OverallContainer>
  );
};

export default BusinessDocuments;

